import type { ActivityStep, WidgetRefConfig } from './classroomApi';

/**
 * Which widget a live step should render.
 *
 * THE BUG THIS EXISTS FOR. `widget_refs` was only ever an ACTIVITY-level field,
 * and `StepPrompt` was handed `activity.widget_refs` for every step. The
 * "Pure or mix? card sort" has sixteen steps — "Where does card 1 belong?",
 * "Where does card 2 belong?", … — and one widget between them. The instructor
 * moved to card 16 and watched the same Particle Model sit there unchanged:
 *
 *   "i switched to card 16 and still shows same Particle model"
 *
 * A step may now carry its own `widget_refs`, which wins. Where a step has
 * none, the activity's is used — correct for an activity whose widget really is
 * shared scenery (one reaction to watch across several prompts), and unchanged
 * behaviour for every activity authored so far.
 *
 * WHAT THIS DOES NOT FIX, and must not be mistaken for fixing: the card-sort
 * content does not define the sixteen cards. Every one of those steps is a
 * prompt with no per-card data anywhere in content/class-activities.json, and
 * the activity's single widget_ref carries an EMPTY config, so the widget shows
 * its own default. This gives that content somewhere to live; authoring
 * sixteen chemically-correct particle scenes is a content decision for the
 * instructor, and inventing them would teach students wrong chemistry.
 */
export function widgetRefsForStep(
  step: Pick<ActivityStep, 'widget_refs'> | undefined,
  activityRefs: WidgetRefConfig[] | undefined,
): WidgetRefConfig[] {
  const own = step?.widget_refs;
  if (Array.isArray(own) && own.length > 0) return own;
  return activityRefs ?? [];
}

/**
 * True when the activity's widget is being shown against a step that reads as
 * if it had one of its own.
 *
 * Used to tell the INSTRUCTOR (never the student) that what they are projecting
 * may not match the prompt. A prompt naming a specific card, sample or trial
 * while the widget is the activity's shared default is the exact shape of the
 * card-sort defect, and it is invisible unless something looks for it.
 */
export function isMismatchedStepWidget(
  step: Pick<ActivityStep, 'prompt' | 'widget_refs'> | undefined,
  activityRefs: WidgetRefConfig[] | undefined,
): boolean {
  if (!step) return false;
  if (Array.isArray(step.widget_refs) && step.widget_refs.length > 0) return false;
  if (!activityRefs || activityRefs.length === 0) return false;
  // "card 16", "sample 3", "trial 2" — a prompt pointing at one of several
  // numbered items, with nothing per-item behind it.
  return /\b(card|sample|trial|item|station|beaker|tube)\s+\d+\b/i.test(step.prompt || '');
}
